// A tiny, connection-aware, concurrency-limited prefetch queue.
// Used by the library grid to warm gem detail data while the browser is idle,
// so a click on any visible card resolves from cache.

type Task = () => Promise<unknown>;

const MAX_IN_FLIGHT = 4;
const queue: { key: string; run: Task; priority: number }[] = [];
const seen = new Set<string>();
let inFlight = 0;
let scheduled = false;

type NetworkInformation = {
  saveData?: boolean;
  effectiveType?: string;
};

/** True when speculative work would be rude: data saver, slow link, or opt-out. */
export function prefetchAllowed(): boolean {
  if (typeof window === "undefined") return false;
  const conn = (navigator as Navigator & { connection?: NetworkInformation })
    .connection;
  if (conn?.saveData) return false;
  const et = conn?.effectiveType ?? "";
  if (et === "2g" || et === "slow-2g") return false;
  if (
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-data: reduce)").matches
  ) {
    return false;
  }
  return true;
}

function idle(cb: () => void) {
  const ric = (
    window as Window & {
      requestIdleCallback?: (cb: IdleRequestCallback, o?: { timeout: number }) => number;
    }
  ).requestIdleCallback;
  if (ric) ric(() => cb(), { timeout: 500 });
  else setTimeout(cb, 32);
}

function takeNext() {
  if (queue.length === 0) return undefined;
  // Highest priority wins; within a lane, newest-queued first — what the user
  // is looking at right now matters more than what they scrolled past.
  let best = queue.length - 1;
  for (let i = queue.length - 2; i >= 0; i--) {
    if (queue[i].priority > queue[best].priority) best = i;
  }
  return queue.splice(best, 1)[0];
}

function pump() {
  scheduled = false;
  while (inFlight < MAX_IN_FLIGHT) {
    const next = takeNext();
    if (!next) return;
    inFlight++;
    Promise.resolve()
      .then(next.run)
      .catch(() => {
        // A failed warm-up is never user visible; let the real navigation retry.
        seen.delete(next.key);
      })
      .finally(() => {
        inFlight--;
        schedule();
      });
  }
}

function schedule() {
  if (scheduled || queue.length === 0) return;
  scheduled = true;
  idle(pump);
}

/**
 * Queue a de-duplicated prefetch. No-ops on slow/metered connections.
 * `priority` 1 = on-screen / likely next, 0 = background speculation.
 */
export function enqueuePrefetch(key: string, run: Task, priority = 1) {
  if (!prefetchAllowed()) return;
  if (seen.has(key)) return;
  seen.add(key);
  queue.push({ key, run, priority });
  if (queue.length > 60) queue.splice(0, queue.length - 60);
  schedule();
}


/** Run immediately (hover / focus / pointerdown intent) ahead of the queue. */
export function prefetchNow(key: string, run: Task) {
  if (seen.has(key)) return;
  seen.add(key);
  Promise.resolve()
    .then(run)
    .catch(() => seen.delete(key));
}
